import React, { useEffect, useRef, useState, useCallback } from 'react';
import styled from 'styled-components';
import { ICategory } from '../../../shared/types/Store';

const Wrapper = styled.div`
  position: relative;
`;

const Container = styled.div<{ $vertical?: boolean }>`
  display: flex;
  gap: 12px;
  scroll-behavior: smooth;

  ${props => props.$vertical ? `
    flex-direction: column;
    gap: 4px;
  ` : `
    overflow-x: auto;
    padding-bottom: 5px;
    &::-webkit-scrollbar { display: none; }
    -ms-overflow-style: none;
    scrollbar-width: none;
  `}
`;

const ScrollArrow = styled.button<{ $side: 'left' | 'right' }>`
  position: absolute;
  top: 50%;
  transform: translateY(-55%);
  ${props => props.$side === 'left' ? 'left: 0;' : 'right: 0;'}
  z-index: 5;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 2px solid #000;
  background: rgba(255, 255, 255, 0.85);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.15s;
  flex-shrink: 0;

  &:hover {
    background: rgba(255, 255, 255, 1);
  }

  &:active {
    transform: translateY(-55%) scale(0.92);
  }
`;

const CategoryButton = styled.button<{ $active: boolean; $vertical?: boolean }>`
  padding: 5px 20px;
  border-radius: 1rem;
  font-weight: 800;
  font-size: 13px;
  white-space: nowrap;
  transition: all 0.2s;
  background-color: ${props => props.$active ? 'var(--primary)' : 'white'};
  color: ${props => props.$active ? 'white' : '#6b7280'};
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);

  ${props => props.$vertical && `
    text-align: left;
    width: 100%;
    padding: 10px 16px;
    border-radius: 0.75rem;
    white-space: normal;
  `}

  &:active {
    transform: scale(0.95);
  }
`;

interface CategoryNavProps {
    categories: ICategory[];
    selectedCategory: string;
    onSelect: (id: string) => void;
    vertical?: boolean;
}

export const CategoryNav: React.FC<CategoryNavProps> = ({ categories, selectedCategory, onSelect, vertical }) => {
    const buttonRefs = useRef<{ [key: string]: HTMLButtonElement | null }>({});
    const scrollRef = useRef<HTMLDivElement>(null);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);

    const updateScrollState = useCallback(() => {
        const el = scrollRef.current;
        if (!el) return;
        setCanScrollLeft(el.scrollLeft > 0);
        setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
    }, []);

    useEffect(() => {
        if (vertical) return;
        const el = scrollRef.current;
        if (!el) return;
        updateScrollState();
        el.addEventListener('scroll', updateScrollState);
        const ro = new ResizeObserver(updateScrollState);
        ro.observe(el);
        return () => {
            el.removeEventListener('scroll', updateScrollState);
            ro.disconnect();
        };
    }, [vertical, updateScrollState, categories]);

    useEffect(() => {
        const activeButton = buttonRefs.current[selectedCategory];
        if (activeButton && !vertical) {
            activeButton.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
                inline: 'center'
            });
        }
    }, [selectedCategory, vertical]);

    const scrollBy = (direction: 'left' | 'right') => {
        const el = scrollRef.current;
        if (!el) return;
        const amount = el.clientWidth * 0.6;
        el.scrollBy({ left: direction === 'left' ? -amount : amount, behavior: 'smooth' });
    };

    return (
        <Wrapper>
            {!vertical && canScrollLeft && (
                <ScrollArrow $side="left" onClick={() => scrollBy('left')}>
                    <i className="fas fa-chevron-left" style={{ fontSize: '11px', color: '#111' }} />
                </ScrollArrow>
            )}
            <Container $vertical={vertical} ref={scrollRef}>
                {categories.map(category => (
                    <CategoryButton
                        key={category.id}
                        $active={selectedCategory === category.id}
                        $vertical={vertical}
                        onClick={() => onSelect(category.id)}
                        ref={(el) => {
                            buttonRefs.current[category.id] = el;
                        }}
                    >
                        {category.name}
                    </CategoryButton>
                ))}
            </Container>
            {!vertical && canScrollRight && (
                <ScrollArrow $side="right" onClick={() => scrollBy('right')}>
                    <i className="fas fa-chevron-right" style={{ fontSize: '11px', color: '#111' }} />
                </ScrollArrow>
            )}
        </Wrapper>
    );
};
